import React, { useState, useEffect, useRef } from "react";
import { Edit2, Save, X, Camera, User } from "lucide-react";
import ProfileSkeleton from "./ProfileSkeleton";

const MyProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const userData =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("BricksNbar-Users-Data") || "{}")
      : {};
  const userId = userData?._id;

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
    } else {
      setLoading(false);
      setError("User ID not found. Please log in again.");
    }
  }, [userId]);

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/customers/get-user-by-id/${userId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user data");
      }

      const data = await response.json();
      setUser(data);
      setEditedUser({ ...data });
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setEditedUser({ ...user });
    }
    setIsEditing(!isEditing);
    setSaveStatus(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "rakesh_kumar_cloudinary");

      const uploadResponse = await fetch(
        "https://api.cloudinary.com/v1_1/du5dn8hhw/image/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error("Upload failed with status: " + uploadResponse.status);
      }

      const uploadData = await uploadResponse.json();
      console.log("Upload response:", uploadData);

      if (uploadData.secure_url) {
        setEditedUser((prev) => ({
          ...prev,
          imageUrl: uploadData.secure_url,
        }));
        console.log("Image URL updated:", uploadData.secure_url);
      } else {
        throw new Error("No secure URL in response");
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaveStatus("saving");
    try {
      const updateData = {
        _id: userId,
        firstName: editedUser.firstName || "",
        lastName: editedUser.lastName || "",
        email: editedUser.email || "",
        phone: editedUser.phone || "",
        gender: editedUser.gender || "",
        imageUrl: editedUser.imageUrl || "",
      };

      console.log("Sending update with data:", updateData);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/customers/update-user/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        }
      );

      const responseText = await response.text();
      console.log("Raw response:", responseText);

      if (!responseText) {
        throw new Error("Empty response received");
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error("Failed to parse response as JSON:", e);
        throw new Error("Invalid response format");
      }

      if (!response.ok) {
        console.error("API error response:", data);
        throw new Error(data.message || "Failed to update profile");
      }

      // Handle the response data
      const updatedUserData = data.user || data;
      console.log("Update successful, new user data:", updatedUserData);

      // Update local state
      setUser(updatedUserData);
      setEditedUser({ ...updatedUserData });
      setIsEditing(false);
      setSaveStatus("success");

      // Also update localStorage if needed
      const currentUserData = JSON.parse(
        localStorage.getItem("BricksNbar-Users-Data") || "{}"
      );
      localStorage.setItem(
        "BricksNbar-Users-Data",
        JSON.stringify({
          ...currentUserData,
          ...updatedUserData,
        })
      );

      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error("Profile update error:", err);
      setSaveStatus("error");
    }
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-red-600 text-lg font-medium">{error}</div>
      </div>
    );
  }

  if (!editedUser) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-red-600 text-lg font-medium">
          User data not found
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="w-full mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-orange-600 h-32 relative">
          <div className="absolute -bottom-16 left-8">
            <div className="relative">
              {editedUser.imageUrl ? (
                <img
                  src={editedUser.imageUrl}
                  alt={`${editedUser.firstName || ""} ${
                    editedUser.lastName || ""
                  }`}
                  className="w-32 h-32 rounded-full border-4 border-white bg-white object-cover cursor-pointer"
                  onClick={handleImageClick}
                />
              ) : (
                <div
                  className="w-32 h-32 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center cursor-pointer"
                  onClick={handleImageClick}
                >
                  <User size={64} className="text-gray-400" />
                </div>
              )}
              {isEditing && (
                <div
                  className="absolute bottom-0 right-0 bg-blue-500 p-2 rounded-full text-white cursor-pointer"
                  onClick={handleImageClick}
                >
                  {uploading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Camera size={16} />
                  )}
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleImageUpload}
              />
            </div>
          </div>
          <div className="absolute top-4 right-4">
            {isEditing ? (
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={saveStatus === "saving"}
                  className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-md flex items-center shadow-sm"
                >
                  {saveStatus === "saving" ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  ) : (
                    <Save size={16} className="mr-1" />
                  )}
                  {saveStatus === "saving" ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={handleEditToggle}
                  className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-md flex items-center shadow-sm"
                >
                  <X size={16} className="mr-1" />
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleEditToggle}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md flex items-center shadow-sm"
              >
                <Edit2 size={16} className="mr-1" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Profile content */}
        <div className="pt-20 px-8 pb-8">
          {saveStatus === "success" && (
            <div className="mb-4 bg-green-100 text-green-700 p-3 rounded-md">
              Profile updated successfully!
            </div>
          )}
          {saveStatus === "error" && (
            <div className="mb-4 bg-red-100 text-red-700 p-3 rounded-md">
              Failed to update profile. Please try again.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {isEditing ? (
                    <div className="flex flex-col">
                      <input
                        type="text"
                        name="firstName"
                        value={editedUser.firstName || ""}
                        onChange={handleInputChange}
                        className="text-2xl font-bold border-b border-gray-300 focus:border-blue-500 focus:outline-none py-1"
                        placeholder="First Name"
                      />
                    </div>
                  ) : editedUser.firstName && editedUser.lastName ? (
                    `${editedUser.firstName} ${editedUser.lastName}`
                  ) : (
                    "No Name Provided"
                  )}
                </h2>
                {isEditing && (
                  <input
                    type="text"
                    name="lastName"
                    value={editedUser.lastName || ""}
                    onChange={handleInputChange}
                    className="text-2xl font-bold border-b border-gray-300 focus:border-blue-500 focus:outline-none py-1 mt-2"
                    placeholder="Last Name"
                  />
                )}
                <p className="text-gray-500 mt-1">
                  Member since{" "}
                  {editedUser.createdAt
                    ? new Date(editedUser.createdAt).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-700">
                  Contact Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    {isEditing ? (
                      <input
                        type="email"
                        name="email"
                        value={editedUser.email || ""}
                        onChange={handleInputChange}
                        className="w-full border-b border-gray-300 focus:border-blue-500 focus:outline-none py-1"
                        placeholder="Email"
                      />
                    ) : (
                      <p className="text-gray-700">
                        {editedUser.email || "N/A"}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="phone"
                        value={editedUser.phone || ""}
                        onChange={handleInputChange}
                        className="w-full border-b border-gray-300 focus:border-blue-500 focus:outline-none py-1"
                        placeholder="Phone Number"
                      />
                    ) : (
                      <p className="text-gray-700">
                        {editedUser.phone || "N/A"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-700">
                  Personal Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Gender</p>
                    {isEditing ? (
                      <select
                        name="gender"
                        value={editedUser.gender || ""}
                        onChange={handleInputChange}
                        className="w-full border-b border-gray-300 focus:border-blue-500 focus:outline-none py-1 bg-white"
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                        <option value="Prefer not to say">
                          Prefer not to say
                        </option>
                      </select>
                    ) : (
                      <p className="text-gray-700">
                        {editedUser.gender || "N/A"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
